// ============================================================================
// DEFAULT PERMISSIONS
// ============================================================================

export const DEFAULT_ROLE_PERMISSIONS: string[] = [
  "booking_type.read",
  "booking_type.areas.read",
  "booking_type.auto_confirm_rules.read",
  "booking_type.availability.read",
  "booking_type.payments.read",
  "booking_type.preorders.read",
  "site.read",
  "site.areas.read",
  "site.auto_double_confirm.read",
  "site.availability.read",
  "site.booking_requirements.read",
  "site.content.read",
  "site.internationalisation.read",
  "site.linked_bookings.read",
  "site.preorders.read",
  "organisation.read",
  "organisation.booking_requirements.read",
  "organisation.content.read",
  "organisation.internationalisation.read"
] as const;

// ============================================================================
// Roles
// ============================================================================

// Helper type for capability functions
export interface CapabilityHelpers {
  hasCapabilityPermission: (capability: Capabilities) => boolean;
  getIdsFromCapabilities: (capabilities: Capabilities[]) => string[];
}

export interface RoleHelpers {
  // canOnAny: (entity: string, action: string) => boolean;
  hasRolePermission: (permission: RolePermissions) => boolean;
}

export interface UsePermissionsReturn {
  roles: AllRolePermissions & RoleHelpers & DerivedRoles;
  capabilities: AllCapabilities & CapabilityHelpers & DerivedCapabilities;
  hasVenues: boolean;
  isWorkspaceInstance: boolean;
  userIsDmn: boolean;
  loading: boolean;
}

export type RolePermissions =
  // Audit Log permissions
  | "audit_log.read"
  | "audit_log.download"

  // Customer permissions
  | "customer.create"
  | "customer.read"
  | "customer.update"
  | "customer.delete"
  | "customer.download"

  // Site permissions
  | "site.create"
  | "site.update"
  | "site.delete"
  | "site.approve_changes.update"
  | "site.content.update"
  | "site.content.contribute"
  | "site.content.read"
  | "site.content.internal"
  | "site.areas.update"
  | "site.auto_double_confirm.update"
  | "site.availability.update"
  | "site.booking_requirements.update"
  | "site.booking_type_priority.read"
  | "site.booking_type_priority.update"
  | "site.concierge.read"
  | "site.concierge.update"
  | "site.daily_notes.read"
  | "site.daily_notes.update"
  | "site.diary_settings.read"
  | "site.diary_settings.update"
  | "site.documents.read"
  | "site.documents.update"
  | "site.financial_details.read"
  | "site.financial_details.update"
  | "site.floorplans.read"
  | "site.floorplans.update"
  | "site.integration_configs.read"
  | "site.integration_configs.update"
  | "site.internal.create"
  | "site.internal.delete"
  | "site.internal.read"
  | "site.internal.update"
  | "site.invoicing_and_contracts.read"
  | "site.invoicing_and_contracts.update"
  | "site.labels_and_colours.read"
  | "site.labels_and_colours.update"
  | "site.linked_bookings.create"
  | "site.linked_bookings.update"
  | "site.linked_bookings.delete"
  | "site.marketing_settings.read"
  | "site.marketing_settings.update"
  | "site.messaging.read"
  | "site.messaging.update"
  | "site.notification_settings.read"
  | "site.notification_settings.update"
  | "site.payments.read"
  | "site.payments.update"
  | "site.preorders.update"
  | "site.reporting.read"
  | "site.reporting.update"
  | "site.venue_hire_settings.read"
  | "site.venue_hire_settings.update"
  | "site.waitlist_settings.read"
  | "site.waitlist_settings.update"

  // Organisation permissions
  | "organisation.create"
  | "organisation.update"
  | "organisation.delete"
  | "organisation.read"
  | "organisation.content.read"
  | "organisation.content.update"
  | "organisation.booking_requirements.update"
  | "organisation.booking_settings.read"
  | "organisation.booking_settings.update"
  | "organisation.financial_details.read"
  | "organisation.financial_details.update"
  | "organisation.integration_configs.create"
  | "organisation.integration_configs.delete"
  | "organisation.integration_configs.read"
  | "organisation.integration_configs.update"
  | "organisation.internal.read"
  | "organisation.internal.update"
  | "organisation.invoicing_and_contracts.read"
  | "organisation.invoicing_and_contracts.update"
  | "organisation.labels_and_colours.read"
  | "organisation.labels_and_colours.update"
  | "organisation.linked_bookings.read"
  | "organisation.linked_bookings.update"
  | "organisation.marketing_settings.read"
  | "organisation.marketing_settings.update"
  | "organisation.messaging.read"
  | "organisation.messaging.update"
  | "organisation.notification_settings.read"
  | "organisation.notification_settings.update"
  | "organisation.payments.read"
  | "organisation.payments.update"
  | "organisation.preorders.read"
  | "organisation.preorders.update"
  | "organisation.reporting.read"
  | "organisation.reporting.update"
  | "organisation.site_tags.update"

  // Booking permissions
  | "booking.create"
  | "booking.read"
  | "booking.update"
  | "booking.delete"
  | "booking.download"
  | "booking.areas.read"
  | "booking.areas.update"
  | "booking.contract.read"
  | "booking.contract.update"
  | "booking.deposits.create"
  | "booking.deposits.download"
  | "booking.deposits.read"
  | "booking.deposits.update"
  | "booking.financial_details.read"
  | "booking.financial_details.update"
  | "booking.integration_configs.read"
  | "booking.integration_configs.update"
  | "booking.internal.read"
  | "booking.internal.update"
  | "booking.invoicing.read"
  | "booking.invoicing.update"
  | "booking.messages.read"
  | "booking.messages.create"
  | "booking.notes.read"
  | "booking.notes.update"
  | "booking.preorders.read"
  | "booking.preorders.update"
  | "booking.preorders.download"
  | "booking.refunds.create"
  | "booking.refunds.read"

  // Booking Type permissions
  | "booking_type.areas.update"
  | "booking_type.auto_confirm_rules.update"
  | "booking_type.availability.update"
  | "booking_type.customer_changes.read"
  | "booking_type.customer_changes.update"
  | "booking_type.messaging.read"
  | "booking_type.messaging.update"
  | "booking_type.payments.update"
  | "booking_type.preorders.update"
  | "booking_type.prompts.read"
  | "booking_type.prompts.update"
  | "booking_type.update"

  // User permissions
  | "user.create"
  | "user.read"
  | "user.update"
  | "user.delete"
  | "user.financial_details.read"
  | "user.financial_details.update"
  | "user.internal.read"
  | "user.internal.update"
  | "user.marketing_settings.read"
  | "user.permissions.read"
  | "user.permissions.update"
  | "user.workspace_details.read"
  | "user.workspace_details.update"

  // Bank Transfer permissions
  | "bank_transfer.read"
  | "bank_transfer.download"

  // Payment permissions
  | "payment.read"
  | "payment.download"

  // Collins permissions
  | "collins.manage"
  | "collins.update";

// ============================================================================
// CAPABILITY TYPES
// ============================================================================

export type CapabilitiesWithVenueId =
  | "access_venue_bookings_"
  | "confirm_venue_bookings_"
  | "delete_venue_bookings_"
  | "manage_venue_bookings_"
  | "download_venue_customers_"
  | "download_venue_reports_"
  | "manage_venue_booking_rules_"
  | "manage_venue_customers_"
  | "manage_venue_permissions_"
  | "manage_venue_refunds_"
  | "view_venue_customers_"
  | "view_venue_reports_";

export type CapabilitiesWithVenueGroupId =
  | "access_venue_group_bookings_"
  | "confirm_venue_group_bookings_"
  | "delete_venue_group_bookings_"
  | "manage_venue_group_bookings_"
  | "download_venue_group_customers_"
  | "download_venue_group_reports_"
  | "manage_no_venue_bookings_"
  | "manage_venue_group_booking_rules_"
  | "manage_venue_group_permissions_"
  | "manage_venue_group_refunds_"
  | "manage_venue_group_venues_"
  | "view_venue_group_customers_"
  | "view_venue_group_reports_";

export type Capabilities =
  // Access & Authentication
  | "access_audit_log"
  | "access_collins_success"
  | "has_early_access_preview"
  | "evo_early_adopter"

  // Booking Management
  | "access_venue_bookings_{{id}}"
  | "access_venue_group_bookings_{{id}}"
  | "confirm_venue_bookings_{{id}}"
  | "confirm_venue_group_bookings_{{id}}"
  | "delete_venue_bookings_{{id}}"
  | "delete_venue_group_bookings_{{id}}"
  | "manage_bookings"
  | "manage_dmn_source_bookings"
  | "manage_no_venue_bookings_{{id}}"
  | "manage_venue_bookings_{{id}}"
  | "manage_venue_group_bookings_{{id}}"
  | "read_all_bookings"
  | "view_dmn_referral_source_bookings"

  // Customer Management
  | "download_venue_customers_{{id}}"
  | "download_venue_group_customers_{{id}}"
  | "manage_venue_customers_{{id}}"
  | "read_all_customers"
  | "view_venue_customers_{{id}}"
  | "view_venue_group_customers_{{id}}"

  // Content & Pages
  | "edit_content"

  // Financial & Banking
  | "manage_venue_accounts"
  | "manage_venue_bank_details"
  | "manage_venue_group_refunds_{{id}}"
  | "manage_venue_refunds_{{id}}"
  | "read_bank_transfer_records"

  // System Administration
  | "impersonate_users"
  | "manage_collins"
  | "manage_permissions"
  | "manage_restricted_permissions"
  | "manage_users"
  | "view_admin_audit"
  | "manage_webhook_subscriptions"

  // Venue & Group Management
  | "manage_venue_booking_rules_{{id}}"
  | "manage_venue_group_booking_rules_{{id}}"
  | "manage_venue_group_permissions_{{id}}"
  | "manage_venue_group_venues_{{id}}"
  | "manage_venue_permissions_{{id}}"
  | "read_all_venues"

  // Reports & Analytics
  | "download_venue_group_reports_{{id}}"
  | "download_venue_reports_{{id}}"
  | "download_venue_reports_internal"
  | "view_stats"
  | "view_venue_group_reports_{{id}}"
  | "view_venue_reports_{{id}}"
  | "view_venue_reports_internal";

// ============================================================================
// COMPREHENSIVE CAPABILITY AND ROLE PERMISSION TYPES
// ============================================================================

/**
 * Comprehensive capabilities interface containing all available capabilities
 * with user-friendly boolean properties, similar to UseCapabilities pattern
 */
export interface CapabilityVenueGroupId extends CapabilityIds {
  thisVenueGroup: boolean;
}
export interface CapabilityVenueId extends CapabilityIds {
  thisVenue: boolean;
}
export interface CapabilityIds {
  ids: string[];
  some: boolean;
  hasId: (id: string) => boolean;
}

export interface DerivedCapabilities {
  derived: {
    userIsInternal: boolean;
  };
}
export interface AllCapabilities {
  // Access & Authentication
  accessAuditLog: boolean;
  accessCollinsSuccess: boolean;
  hasEarlyAccessPreview: boolean;
  evoEarlyAdopter: boolean;

  // Booking Management
  accessVenueBookings: CapabilityVenueId;
  accessVenueGroupBookings: CapabilityVenueGroupId;
  confirmVenueBookings: CapabilityVenueId;
  confirmVenueGroupBookings: CapabilityVenueGroupId;
  deleteVenueBookings: CapabilityVenueId;
  deleteVenueGroupBookings: CapabilityVenueGroupId;
  manageBookings: boolean;
  manageDmnSourceBookings: boolean;
  manageNoVenueBookings: CapabilityVenueGroupId;
  manageVenueBookings: CapabilityVenueId;
  manageVenueGroupBookings: CapabilityVenueGroupId;
  readAllBookings: boolean;
  viewDmnReferralSourceBookings: boolean;

  // Customer Management
  downloadVenueCustomers: CapabilityVenueId;
  downloadVenueGroupCustomers: CapabilityVenueGroupId;
  manageVenueCustomers: CapabilityVenueId;
  readAllCustomers: boolean;
  viewVenueCustomers: CapabilityVenueId;
  viewVenueGroupCustomers: CapabilityVenueGroupId;

  // Content & Pages
  editContent: boolean;

  // Financial & Banking
  manageVenueAccounts: boolean;
  manageVenueBankDetails: boolean;
  manageVenueGroupRefunds: CapabilityVenueGroupId;
  manageVenueRefunds: CapabilityVenueId;
  readBankTransferRecords: boolean;

  // System Administration
  impersonateUsers: boolean;
  manageCollins: boolean;
  managePermissions: boolean;
  manageRestrictedPermissions: boolean;
  manageUsers: boolean;
  viewAdminAudit: boolean;
  manageWebhookSubscriptions: boolean;

  // Venue & Group Management
  manageVenueBookingRules: CapabilityVenueId;
  manageVenueGroupBookingRules: CapabilityVenueGroupId;
  manageVenueGroupPermissions: CapabilityVenueGroupId;
  manageVenueGroupVenues: CapabilityVenueGroupId;
  manageVenuePermissions: CapabilityVenueId;
  readAllVenues: boolean;

  // Reports & Analytics
  downloadVenueGroupReports: CapabilityVenueGroupId;
  downloadVenueReports: CapabilityVenueId;
  downloadVenueReportsInternal: boolean;
  viewStats: boolean;
  viewVenueGroupReports: CapabilityVenueGroupId;
  viewVenueReports: CapabilityVenueId;
  viewVenueReportsInternal: boolean;
}

/**
 * Comprehensive role permissions interface containing all available role permissions
 * with resource-based organisation, following UseRoles pattern
 */
export interface DerivedRoles {
  derived: {
    hasRoles: boolean;
    userIsInternal: boolean;
  };
}
export interface AllRolePermissions {
  // Audit Log permissions
  auditLog: {
    read: boolean;
    download: boolean;
  };

  // Customer permissions
  customer: {
    create: boolean;
    read: boolean;
    update: boolean;
    delete: boolean;
    download: boolean;
  };

  // Site permissions
  site: {
    create: boolean;
    update: boolean;
    delete: boolean;
    approveChanges: {
      update: boolean;
    };
    content: {
      update: boolean;
      contribute: boolean;
      read: boolean;
      internal: boolean;
    };
    areas: {
      update: boolean;
    };
    autoDoubleConfirm: {
      update: boolean;
    };
    availability: {
      update: boolean;
    };
    bookingRequirements: {
      update: boolean;
    };
    bookingTypePriority: {
      read: boolean;
      update: boolean;
    };
    concierge: {
      read: boolean;
      update: boolean;
    };
    dailyNotes: {
      read: boolean;
      update: boolean;
    };
    diarySettings: {
      read: boolean;
      update: boolean;
    };
    documents: {
      read: boolean;
      update: boolean;
    };
    financialDetails: {
      read: boolean;
      update: boolean;
    };
    floorplans: {
      read: boolean;
      update: boolean;
    };
    integrationConfigs: {
      read: boolean;
      update: boolean;
    };
    internal: {
      create: boolean;
      delete: boolean;
      read: boolean;
      update: boolean;
    };
    invoicingAndContracts: {
      read: boolean;
      update: boolean;
    };
    labelsAndColours: {
      read: boolean;
      update: boolean;
    };
    linkedBookings: {
      create: boolean;
      update: boolean;
      delete: boolean;
    };
    marketingSettings: {
      read: boolean;
      update: boolean;
    };
    messaging: {
      read: boolean;
      update: boolean;
    };
    notificationSettings: {
      read: boolean;
      update: boolean;
    };
    payments: {
      read: boolean;
      update: boolean;
    };
    preorders: {
      update: boolean;
    };
    reporting: {
      read: boolean;
      update: boolean;
    };
    venueHireSettings: {
      read: boolean;
      update: boolean;
    };
    waitlistSettings: {
      read: boolean;
      update: boolean;
    };
  };

  // Organisation permissions
  organisation: {
    create: boolean;
    update: boolean;
    delete: boolean;
    read: boolean;
    content: {
      read: boolean;
      update: boolean;
    };
    bookingRequirements: {
      update: boolean;
    };
    bookingSettings: {
      read: boolean;
      update: boolean;
    };
    financialDetails: {
      read: boolean;
      update: boolean;
    };
    integrationConfigs: {
      create: boolean;
      delete: boolean;
      read: boolean;
      update: boolean;
    };
    internal: {
      read: boolean;
      update: boolean;
    };
    invoicingAndContracts: {
      read: boolean;
      update: boolean;
    };
    labelsAndColours: {
      read: boolean;
      update: boolean;
    };
    linkedBookings: {
      read: boolean;
      update: boolean;
    };
    marketingSettings: {
      read: boolean;
      update: boolean;
    };
    messaging: {
      read: boolean;
      update: boolean;
    };
    notificationSettings: {
      read: boolean;
      update: boolean;
    };
    payments: {
      read: boolean;
      update: boolean;
    };
    preorders: {
      read: boolean;
      update: boolean;
    };
    reporting: {
      read: boolean;
      update: boolean;
    };
    siteTags: {
      update: boolean;
    };
  };

  // Booking permissions
  booking: {
    create: boolean;
    read: boolean;
    update: boolean;
    delete: boolean;
    download: boolean;
    areas: {
      read: boolean;
      update: boolean;
    };
    contract: {
      read: boolean;
      update: boolean;
    };
    deposits: {
      create: boolean;
      download: boolean;
      read: boolean;
      update: boolean;
    };
    financialDetails: {
      read: boolean;
      update: boolean;
    };
    integrationConfigs: {
      read: boolean;
      update: boolean;
    };
    internal: {
      read: boolean;
      update: boolean;
    };
    invoicing: {
      read: boolean;
      update: boolean;
    };
    messages: {
      read: boolean;
      create: boolean;
    };
    notes: {
      read: boolean;
      update: boolean;
    };
    preorders: {
      read: boolean;
      update: boolean;
      download: boolean;
    };
    refunds: {
      create: boolean;
      read: boolean;
    };
  };

  // Booking Type permissions
  bookingType: {
    areas: {
      update: boolean;
    };
    autoConfirmRules: {
      update: boolean;
    };
    availability: {
      update: boolean;
    };
    customerChanges: {
      read: boolean;
      update: boolean;
    };
    messaging: {
      read: boolean;
      update: boolean;
    };
    payments: {
      update: boolean;
    };
    preorders: {
      update: boolean;
    };
    prompts: {
      read: boolean;
      update: boolean;
    };
    update: boolean;
  };

  // User permissions
  user: {
    create: boolean;
    read: boolean;
    update: boolean;
    delete: boolean;
    financialDetails: {
      read: boolean;
      update: boolean;
    };
    internal: {
      read: boolean;
      update: boolean;
    };
    marketingSettings: {
      read: boolean;
    };
    permissions: {
      read: boolean;
      update: boolean;
    };
    workspaceDetails: {
      read: boolean;
      update: boolean;
    };
  };

  // Bank Transfer permissions
  bankTransfer: {
    read: boolean;
    download: boolean;
  };

  // Payment permissions
  payment: {
    read: boolean;
    download: boolean;
  };

  // Collins permissions
  collins: {
    manage: boolean;
  };
}

// ============================================================================
